//Helpers
import { isThreeEM, isWeatherStation } from '../../interface_helpers';
import { average, sum } from '../../utilities';
//Stores
import { checkForStatisticError } from '../core/chart_helper';
import { convertToUnit, skipUnitConversion } from 'src/helpers/units_helpers';
//Types
import type { ChartConfig, ChartStatisticInterface } from 'src/types/Chart';

export const getAverageStatistic = (
	chartData: ChartConfig,
	threeEmChannel: number = null,
): ChartStatisticInterface => {
	if (
		threeEmChannel === null &&
		chartData.name == 'voltage' &&
		isThreeEM(chartData.deviceType) &&
		chartData.deviceMode != 'monophase'
	) {
		const result: ChartStatisticInterface = {
			available: true,
		};
		const indexes = [];
		for (let i = 0; i < chartData.series.length; ++i) {
			const phaseData = chartData.series[i];
			let item = indexes[phaseData.index];
			if (!item) {
				item = { ...chartData, phaseName: phaseData.name.substring(0, 7) };
				indexes[phaseData.index] = item;
				item.series = [];
			}
			item.series.push(phaseData);
		}
		result.threeEMStatistic = [];
		for (let i = 0; i < indexes.length; ++i) {
			const phase = getAverageStatistic(indexes[i], i);
			result.threeEMStatistic.push({
				...phase,
				phaseName: indexes[i].phaseName,
			});
		}
		return result;
	} else if (
		/(temperature|input|voltmeter|measurement|distance|pressure)$/.test(chartData.name) ||
		(chartData.deviceCategory == 'emeter' && chartData.name == 'voltage')
	) {
		let attr = chartData.name;
		if (attr == 'input') {
			attr = 'percent_' + chartData.extid;
		} else if (attr == 'voltmeter') {
			attr = 'voltage_' + chartData.extid;
		}
		let minAttr = `min_${attr}`;
		let maxAttr = `max_${attr}`;
		if (/(measurement|distance)/.test(chartData.name)) {
			minAttr = 'min';
			maxAttr = 'max';
		}
		const dataMinIndex = chartData.series.findIndex((i) => i.name.includes(minAttr)),
			dataMaxIndex = chartData.series.findIndex((i) => i.name.includes(maxAttr));
		let minValues: any[] = chartData.series[dataMinIndex].data,
			maxValues: any[] = chartData.series[dataMaxIndex].data;
		if (chartData.name == 'voltage') {
			minValues = minValues.filter((v) => !v?.missing && v.value != 0);
			maxValues = maxValues.filter((v) => !v?.missing && v.value != 0);
		}
		minValues = minValues.filter((v) => !checkForStatisticError(v.value)).map((v) => v.value);
		maxValues = maxValues.filter((v) => !checkForStatisticError(v.value)).map((v) => v.value);
		const averageValue = average([...minValues, ...maxValues]);
		if (!isNaN(averageValue) && averageValue != 0) {
			let min = Math.min(...minValues),
				max = Math.max(...maxValues);
			const convertToUnitParams = {
				fixed: 1,
				dontConvert: skipUnitConversion(chartData.title),
			};
			return {
				available: true,
				statistic: true,
				average: convertToUnit(averageValue, chartData.units, convertToUnitParams),
				min: convertToUnit(min == Infinity ? 0 : min, chartData.units, convertToUnitParams),
				max: convertToUnit(max == Infinity ? 0 : max, chartData.units, convertToUnitParams),
			};
		}
	} else if (
		isWeatherStation(chartData.deviceType) ||
		/(humidity|valve_position|accumulation|ppm|voltage|counter)$/.test(chartData.name)
	) {
		let attr = chartData.name;
		if (attr == 'valve_position') {
			attr = 'position';
		} else if (/(accumulation)/.test(attr)) {
			attr = 'value';
		}
		const dataIndex = chartData.series.findIndex((i) => i.name == attr);
		let values: any[] = chartData.series[dataIndex]?.data || [];
		values = values.filter((v) => !checkForStatisticError(v.value)).map((v) => v.value);
		const averageValue = average([...values]);
		let totalCounter: string = null;
		const convertToUnitParams = {
			fixed: 1,
			dontConvert: skipUnitConversion(chartData.name),
		};
		if (/(accumulation|precipitation)$/.test(chartData.name)) {
			totalCounter = convertToUnit(sum(values), chartData.units, convertToUnitParams);
		}
		if (!isNaN(averageValue) && averageValue != 0) {
			return {
				available: true,
				statistic: true,
				totalCounter,
				average: convertToUnit(averageValue, chartData.units, convertToUnitParams),
				min: convertToUnit(Math.min(...values), chartData.units, convertToUnitParams),
				max: convertToUnit(Math.max(...values), chartData.units, convertToUnitParams),
			};
		}
	}
	return null;
};
